// External Imports
import { Fragment, useEffect, useRef, useState } from "react";
import LoadingSpinner from "./LoadingSpinner";
import { MdAdd, MdSearch } from "react-icons/md";
import { requestAdminAPI } from "../../api/posh-api";
import { toTitleCase } from "../../util/format-text";

// Internal Imports

/** ========================================================================
 * * SupplierSelector Component
 * This component renders a dropdown to select a supplier.
 * ========================================================================== */

export default function SupplierSelector({
  onSelect,
  selected,
  label = "Supplier",
}) {
  const [loading, setLoading] = useState(false);
  const [records, setSuppliers] = useState([]);
  const [show, setShow] = useState(false);

  // Declare Refs
  const inputRef = useRef(null);
  const valueRef = useRef(null);

  /* ====================== FETCH RECORDS  ====================== */
  const fetchRecords = async (e) => {
    e.preventDefault();
    const query = e.target.query.value?.toLowerCase();
    if (!query) return;
    try {
      setLoading(true);
      const response = await requestAdminAPI("filter", {
        table: "supplier",
        filters: [`name: {contains: "${query}"}`],
      });
      setSuppliers(response);
      setShow(true);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= FETCH VEHICLE ======================= */
  const fetchRecord = async () => {
    try {
      setLoading(true);
      const res = await requestAdminAPI("fetch", {
        table: "supplier",
        id: selected,
      });
      optionClickHandler(res);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= ADD RECORD ======================= */
  const addRecord = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("add-supplier", {
        name: inputRef.current.value,
      });
      optionClickHandler(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= OPTION CLICK HANDLER ======================= */
  const optionClickHandler = (record) => {
    inputRef.current.value = toTitleCase(record.name);
    valueRef.current.value = record.id;
    setShow(false);
    onSelect(record);
  };

  // If selection is made outside of the dropdown
  useEffect(() => {
    if (selected) fetchRecord();
  }, [selected]);

  return (
    <Fragment>
      {show && (
        <div
          onClick={() => setShow(false)}
          className="absolute top-0 left-0 w-full h-full -z-1 no-scrollbar"
        ></div>
      )}
      <div className="relative grid gap-2">
        <form onSubmit={fetchRecords} className="">
          <label className="text-sm text-stone-400">{label}:</label>
          <div className="flex items-center border rounded-md border-stone-600 ">
            <input
              className="relative w-full input"
              autoComplete="off"
              name="query"
              type="text"
              autoCorrect="off"
              spellCheck="false"
              ref={inputRef}
              placeholder="Type here and press enter to search..."
            />
            <button type="submit" className="px-2">
              {loading ? (
                <LoadingSpinner classes="text-primary w-6 h-6" />
              ) : (
                <MdSearch className="icon" />
              )}
            </button>
          </div>
        </form>
        <input
          className="hidden"
          name="supplier"
          id="supplier"
          ref={valueRef}
        />
        {show && (
          <div className="absolute top-16 py-2 px-4 w-full overflow-scroll rounded-lg bg-stone-700 no-scrollbar max-h-[300px] z-10">
            {records.map((supplier) => (
              <div
                key={supplier.id}
                className="px-2 py-1 text-sm font-semibold rounded-md cursor-pointer text-stone-200 hover:bg-stone-600"
                onClick={() => optionClickHandler(supplier)}
              >
                {supplier.name}
              </div>
            ))}
            <div
              className="flex items-center justify-between px-2 py-1 text-sm rounded-md cursor-pointer text-stone-200 hover:bg-stone-600"
              onClick={addRecord}
            >
              <p className="font-semibold">
                Add as new supplier{" "}
                <span className="font-normal text-stone-400">
                  - make sure to enter correct and full name
                </span>
              </p>
              <MdAdd className="text-2xl" />
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
}
